<script setup lang="ts">
definePageMeta({
  title: 'Overmolded Cables',
  description: 'Take advantage of automatic overmolding on your next custom cable assembly.',
  order: 0,
})
</script>

<template>
  <section>
    <BlockContent>
      <h1 class="mb-4 uppercase font-flex-100-151-640">
        Overmolded Cables
      </h1>
      <h2 class="subpage-title">
        ICS Offers the Latest Overmolding Techniques on all Products
      </h2>
      <h3 class="subpage-subtitle mt-2">
        ARBURG ALLROUNDER V on the factory floor
      </h3>
      <div class="my-10 gap-x-4 gap-y-8 grid grid-cols-1 md:grid-cols-2">
        <div>
          <div class="text-lg max-w-prose space-y-4">
            <p>
              Our overmolding capability results in durable, seamless assemblies that help keep your equipment reliable, even in the harshest conditions.
            </p>
            <p>
              It also affords our customers a high degree of customization—like unique form factors and integration with other components.
            </p>
            <p>
              Ask us how overmolding can enhance your assembly's performance without sacrificing space or aesthetics.
            </p>
          </div>
          <nav class="my-10 flex flex-wrap gap-6">
            <AButton
              label="Get a Quote"
              to="/contact/rfq"
              color="blue"
            />
            <AButton
              label="The ARBURG ALLROUNDER V"
              to="https://www.arburg.com/en/us/product-world/injection-molding-machines/vertical-machines/allrounder-v/"
              color="gray"
              material="empty"
              new-tab
            />
          </nav>
          <NuxtLink
            to="https://www.arburg.com/en/us/"
            class="group py-4 flex-inline gap-3 items-center"
          >
            <NuxtImg
              src="b47d7658-90bb-4528-aac2-8a476fb113a8"
              alt="The ARBURG GmbH + Co KG logo."
              class="w-24"
            />
            <div class="i-carbon-launch transition-colors group-hover:bg-icssilver-800 dark:group-hover:bg-icssilver-200" />
          </NuxtLink>
        </div>
        <NuxtImg
          class="w-full transition-opacity object-cover dark:opacity-80"
          src="3b3b001a-4e86-4faa-92f9-fdf60d3483b1"
          alt="The ARBURG ALLROUNDER V injection molding machine photographed in a stark white studio setting."
        />
      </div>

      <ACard element="aside">
        <template #header>
          <h6 class="text-xl font-flex-400">
            Overmolding in a Nutshell
          </h6>
          <hr class="my-4 b-icsgray dark:b-icsnight">
        </template>
        <div class="space-y-4">
          <p>
            An overmolded cable assembly is commonly employed to bolster a cable assembly, augmenting its protection, support, and/or strain relief beyond solely relying on a backshell or heat shrink. The procedure entails using molten PVC (polyvinyl chloride) or TPU (thermoplastic polyurethane), or other advanced polymers to encase wiring materials. Overmolded cables come in a myriad of shapes, sizes, materials, and configurations, providing numerous advantages and the necessary design flexibility for engineers.
          </p>
          <ol class="px-8 list-decimal space-y-4 marker:text-icsblue marker:font-500 marker:dark:text-icscyan">
            <li class="">
              <span class="text-icsblue font-500 dark:text-icscyan">Enhanced Protection:</span> Overmolding adds a protective layer around the cable or wire harness, shielding it from environmental factors such as moisture, dust, chemicals, and mechanical stress. This helps to extend the lifespan of the cable assembly and improve its reliability in harsh operating conditions.
            </li>
            <li class="">
              <span class="text-icsblue font-500 dark:text-icscyan">Improved Durability:</span> The overmolded layer provides increased durability by reinforcing the cable or wire harness against abrasion, impact, and other forms of physical damage. This makes overmolded cable assemblies well-suited for applications where ruggedness and resilience are required.
            </li>
            <li class="">
              <span class="text-icsblue font-500 dark:text-icscyan">Strain Relief:</span> Overmolding helps to distribute and absorb mechanical stresses and strains along the length of the cable assembly, reducing the risk of damage to the electrical conductors or connectors. This is particularly important in applications where the cable is subject to frequent bending, flexing, or pulling.
            </li>
            <li class="">
              <span class="text-icsblue font-500 dark:text-icscyan">Customization:</span> Overmolding allows for a high degree of customization in terms of shapes, sizes, materials, and configurations. This flexibility enables engineers to design cable assemblies that meet the specific requirements of their applications, including unique form factors and integration with other components.
            </li>
            <li class="">
              <span class="text-icsblue font-500 dark:text-icscyan">Seamless Integration:</span> Overmolded cable assemblies offer a seamless integration of cables, connectors, and protective layers, resulting in a compact and space-efficient design. This can help to simplify installation and maintenance processes, as well as improve the overall aesthetics of the final product.
            </li>
            <li class="">
              <span class="text-icsblue font-500 dark:text-icscyan">Wide Range of Applications:</span> Overmolded cable assemblies are used in a wide range of industries and applications, including automotive, aerospace, industrial machinery, medical devices, consumer electronics, and more. They are suitable for both indoor and outdoor use, as well as in demanding environments such as automotive engine compartments or industrial manufacturing facilities.
            </li>
          </ol>
          <p>
            Overall, overmolding is a versatile and effective method for enhancing the performance, durability, and reliability of cable assemblies, making them well-suited for various demanding applications in diverse industries.
          </p>
        </div>
      </ACard>
    </BlockContent>
  </section>
</template>
